import React, { useEffect, useState, useRef, useCallback } from "react";
import axios from "axios";
import { toPng } from "html-to-image";
import CircuitLayout from "../../../components/CircuitLayout";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { Form, Button, Col, Row } from "react-bootstrap";
import {
  faCalendar,
  faDotCircle,
  faFlagCheckered,
  faLocationDot,
  faMapLocation,
  faMapLocationDot,
  faQuestionCircle,
  faStopwatch,
  faTrophy,
} from "@fortawesome/free-solid-svg-icons";
import { useTranslation } from "react-i18next";
import DateFormat from "../../../components/DateFormat";
import LoadingContent from "../../../components/LoadingContent";
import Title from "../../../components/Admin/Title";
import { getAbbreviation } from "../../../utils/Standings";
import "../../../assets/style/imgnews_v1.css";
import GeminiRequest from "../../../components/GeminiRequest";

interface GPData {
  name: string;
  circuit: string;
  date: string;
  time: string;
  laps: string | null;
  flag_code: string;
  last_event_name: string;
  last_podium: string;
  most_wins_driver: string;
  most_poles_driver: string;
  most_wins: number;
  most_poles: number;
  last_pole_driver: string;
  last_pole_time: string;
  circuit_id: number;
  last_grand_prix_event_id: number;
  flag: string;
  round_number?: number;
  total_championship_events?: number;
  championship?: string;
  driver_standings?: any;
  team_standings?: any;
}

const PreGPGenerator = () => {
  const { t } = useTranslation();
  const [data, setData] = useState<GPData | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [generatedImageUrl, setGeneratedImageUrl] = useState<string | null>(
    null,
  );
  const [geminiPromptText, setGeminiPromptText] = useState<string>("");
  const componentRef = useRef<HTMLDivElement>(null);

  const [update, setUpdate] = useState(false);
  const [grandPrixEventId, setGrandPrixEventId] = useState(-1);
  const [nextGrandsPrix, setNextGrandsPrix] = useState<
    {
      name: string;
      id: number;
      type_race: string;
      type_races: number;
      type_championship: number;
    }[]
  >();

  // NUEVO: Estado para almacenar la imagen de fondo personalizada subida por el usuario
  const [customBgImage, setCustomBgImage] = useState<string | null>(null);

  // Manejar la carga del archivo de imagen personalizado
  const handleCustomImageChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) {
      const imageUrl = URL.createObjectURL(file);
      setCustomBgImage(imageUrl);
    }
  };

  // Cargar lista de grandes premios
  useEffect(() => {
    axios
      .get(
        `${
          process.env.NODE_ENV === "development"
            ? "http://localhost:5500"
            : "https://ligavirtualf1.es:5500"
        }/api/grandprix/getNextGrandsPrix`,
      )
      .then(({ data }) => {
        setNextGrandsPrix(data.nextGrandsPrix);
      })
      .catch(({ response }) => {
        throw new Error(response);
      });
  }, [update]);

  // Cargar datos del GP seleccionado
  useEffect(() => {
    if (
      grandPrixEventId !== null &&
      grandPrixEventId !== undefined &&
      grandPrixEventId !== -1
    ) {
      setIsLoading(true);
      setData(null);
      setGeneratedImageUrl(null);
      setError(null);

      const API_BASE =
        process.env.NODE_ENV === "development"
          ? "http://localhost:5500"
          : "https://ligavirtualf1.es:5500";

      axios
        .get(`${API_BASE}/api/grandprix/getNewsGenerateInfo`, {
          params: { grand_prix_event_id: grandPrixEventId },
        })
        .then(({ data }) => {
          console.log(data);
          setData(data);
        })
        .catch((err) => {
          console.error("Error fetching GP info:", err);
          setError("No se pudo cargar la información del Gran Premio.");
          setIsLoading(false);
        });
    }
  }, [grandPrixEventId]);

  // Función para generar la imagen
  const handleGenerateImage = useCallback(() => {
    if (componentRef.current === null) return;

    toPng(componentRef.current, {
      quality: 1.0,
      canvasWidth: 1920,
      canvasHeight: 1080,
      style: {
        overflow: "hidden",
      },
    })
      .then((dataUrl: string) => {
        setGeneratedImageUrl(dataUrl);
      })
      .catch((err) => {
        console.error("Error generating image:", err);
      })
      .finally(() => {
        setIsLoading(false);
      });
  }, [componentRef]);

  // Efecto automático: en cuanto llegan los datos y se renderiza el componente, se genera la imagen
  useEffect(() => {
    if (data !== null) {
      const rafId = requestAnimationFrame(() => {
        handleGenerateImage();
      });
      return () => cancelAnimationFrame(rafId);
    }
  }, [data, handleGenerateImage, customBgImage]);

  const parseGrandPrixName = (fullName: string) => {
    if (!fullName) return <></>;

    const yearMatch = fullName.match(/\b\d{4}\b/);

    if (!yearMatch) {
      return (
        <>
          <label className="nig-title">{fullName.trim().toUpperCase()}</label>
          <label className="nig-gp">GRAND PRIX</label>
        </>
      );
    }

    const yearIndex = yearMatch.index!;
    const yearStr = yearMatch[0];

    const leftPart = fullName.substring(0, yearIndex + yearStr.length).trim();
    const remainder = fullName.substring(yearIndex + yearStr.length).trim();

    const gpMatch = remainder.match(/Grand Prix|GP/i);

    let title2 = remainder;
    if (gpMatch) {
      title2 = remainder.substring(0, gpMatch.index!).trim();
    }

    return (
      <>
        <label className="nig-year">{leftPart}</label>
        <label className="nig-title">{title2.toUpperCase()}</label>
        <label className="nig-gp">GRAND PRIX</label>
      </>
    );
  };

  useEffect(() => {
    if (data !== null) {
      const timer = setTimeout(() => {
        handleGenerateImage();
      }, 400);

      return () => clearTimeout(timer);
    }
  }, [data, handleGenerateImage]);

  // Generación del prompt para Gemini basado en los datos del GP
  useEffect(() => {
    if (data) {
      let standingsContext = "";
      if (data.round_number && data.round_number > 1) {
        let driversByClassText = "";
        if (data.driver_standings && data.driver_standings.length > 0) {
          const classesMap = new Map<string, any[]>();
          data.driver_standings.forEach((d: any) => {
            const className = d.class || "General";
            if (!classesMap.has(className)) classesMap.set(className, []);
            classesMap.get(className)!.push(d);
          });

          const driversLines: string[] = [];
          classesMap.forEach((drivers, className) => {
            const top = drivers
              .slice(0, 3)
              .map((d: any) => `${d.position}. ${d.username} (${d.points} pts)`)
              .join(", ");
            driversLines.push(`Clase ${className}: ${top}`);
          });
          driversByClassText = driversLines.join(" | ");
        }

        let teamsByClassText = "";
        if (data.team_standings && data.team_standings.length > 0) {
          const teamClassesMap = new Map<string, any[]>();
          data.team_standings.forEach((t: any) => {
            const className = t.class || "General";
            if (!teamClassesMap.has(className))
              teamClassesMap.set(className, []);
            teamClassesMap.get(className)!.push(t);
          });

          const teamLines: string[] = [];
          teamClassesMap.forEach((teams, className) => {
            const top = teams
              .slice(0, 3)
              .map(
                (t: any) => `${t.position}. ${t.team_name} (${t.points} pts)`,
              )
              .join(", ");
            teamLines.push(`Clase ${className}: ${top}`);
          });
          teamsByClassText = teamLines.join(" | ");
        }

        if (driversByClassText || teamsByClassText) {
          standingsContext = `
Contexto secundario de la clasificación actual (mención opcional y muy superficial si aporta narrativa):
- Líderes de pilotos: ${driversByClassText || "No disponible"}
- Líderes de equipos: ${teamsByClassText || "No disponible"}
`;
        }
      }

      const historyContext = `
Estadísticas históricas y de referencia del evento:
- Circuito: ${data.circuit}
- País del evento: ${data.flag}
- Vueltas: ${data.laps || "N/A"}
- Ganador de más ediciones (Piloto): ${data.most_wins_driver ? data.most_wins_driver : "N/A"} (${data.most_wins || 0} victorias)
- Máximo dominador en poles: ${data.most_poles_driver ? data.most_poles_driver : "N/A"} (${data.most_poles || 0} poles)
- Último ganador registrado: ${data.last_podium ? data.last_podium.split(" | ")[0] : "N/A"}
- Última referencia de pole: ${data.last_pole_driver ? data.last_pole_driver : "N/A"} (${data.last_pole_time || "N/A"})
`;

      const promptText = `
Por favor, redacta una noticia previa atractiva, competitiva y profesional para el siguiente Gran Premio de la liga LigaVirtualF1:
- Campeonato: ${data.championship || "F1"}
- Nombre del Gran Premio: ${data.name}
- Ronda actual: Ronda ${data.round_number || "N/A"} de ${data.total_championship_events || "N/A"}
- Fecha y hora: ${data.date} a las ${data.time} CET
- Ubicación: ${data.circuit} (${data.flag})

${historyContext}
${standingsContext}

Instrucciones de redacción:
Enfoca la previa en la importancia de esta ronda, los desafíos técnicos que implica competir en ${data.circuit} (${data.flag}), y haz mención opcional a los récords históricos o estadísticas si encajan de forma fluida. Si decides mencionar la clasificación general, hazlo de manera extremadamente sutil, tangencial y secundaria.
Si usas nombres de pilotos, ponlos conservando el texto tal cual con mayusculas y minusculas.

Estructura requerida:
- Título: Máximo 60 caracteres (70 en casos muy extremos).
- Subtítulo: Alrededor de 100 caracteres, máximo 150.
- Cuerpo de la noticia: Completo y bien estructurado.

Lo necesito en español y en inglés, y formateado con negritas en las partes importanes, subtitulos o titulos mas grandes etc.
    `.trim();

      setGeminiPromptText(promptText);
    }
  }, [data]);

  if (nextGrandsPrix == null) return <LoadingContent />;

  return (
    <div className="full-height padding-md app-container flex-center-top page-transition noverflow">
      <div className="form-container-md">
        <Title text="Pre GP Generator" />
        <small className="mandatory-fields">(*) Required fields</small>

        <Form>
          <Row className="mb-3">
            <Form.Group className="form-group" as={Col} md="12">
              <Form.Label>Grand Prix (*)</Form.Label>
              <Form.Select
                name="grand_prix"
                required
                onChange={(e) => {
                  setGrandPrixEventId(parseInt(e.target.value));
                }}
              >
                <option value="-1">Select a Grand Prix...</option>
                {nextGrandsPrix.map((gp) => {
                  return (
                    <option key={gp.id} value={gp.id}>
                      {gp.name}
                    </option>
                  );
                })}
              </Form.Select>
            </Form.Group>
          </Row>

          {/* NUEVO: Input para seleccionar imagen de fondo personalizada */}
          <Row className="mb-3">
            <Form.Group className="form-group" as={Col} md="12">
              <Form.Label>
                Custom Background Image
                <span
                  className="profile-bubble"
                  data-tooltip="Upload an image to replace the default background for the Grand Prix."
                  data-flow="right"
                >
                  <FontAwesomeIcon
                    icon={faQuestionCircle}
                    className="label-icon"
                  />
                </span>
              </Form.Label>
              <Form.Control
                type="file"
                accept="image/*"
                onChange={handleCustomImageChange}
              />
            </Form.Group>
          </Row>
        </Form>

        {isLoading && <LoadingContent />}

        {data != null && (
          <div
            style={{
              position: "absolute",
              zIndex: -1,
              opacity: 0,
              pointerEvents: "none",
            }}
          >
            <div ref={componentRef} className="nig-container">
              <div
                className="nig-background-overlay"
                style={
                  {
                    // NUEVO: Si customBgImage existe, lo usa; de lo contrario, usa la imagen por defecto del servidor
                    "--bg-gp-image": `url(${
                      customBgImage
                        ? customBgImage
                        : `/assets/ImagesGP/${data.last_grand_prix_event_id}.jpg`
                    })`,
                  } as React.CSSProperties
                }
              />
              <div className="nig-diagonal-overlay" />
              <p className="nig-website">LIGAVIRTUALF1.ES</p>
              <div className="nig-header-triangles">
                <div className="nig-triangle" />
                <div className="nig-triangle" />
                <div className="nig-triangle" />
              </div>
              <div className="nig-header-triangles2">
                <div className="nig-triangle2" />
                <div className="nig-triangle2" />
                <div className="nig-triangle2" />
                <div className="nig-triangle2" />
                <div className="nig-triangle2" />
                <div className="nig-triangle2" />
                <div className="nig-triangle2" />
                <div className="nig-triangle2" />
                <div className="nig-triangle2" />
                <div className="nig-triangle2" />
                <div className="nig-triangle2" />
                <div className="nig-triangle2" />
              </div>
              <img
                className="nig-logo"
                src={require(`../../../assets/Images/Logo.png`)}
              />
              <div className="nig-rotated-accent-rect2" />
              <div className="nig-rotated-accent-rect" />
              <div className="nig-content">
                <img
                  src={`/assets/Flags/HighRes/${data.flag_code}.png`}
                  alt={`${data.flag} Flag`}
                  className="nig-flag-icon"
                  onError={(e) => {
                    // Evitamos bucles infinitos por si la segunda ruta tampoco existe
                    e.currentTarget.onerror = null;
                    e.currentTarget.src = `/assets/Flags/${data.flag_code}.png`;
                  }}
                />
                {parseGrandPrixName(data.name)}
                <div className="nig-data-circuit flex">
                  <div className="flex">
                    <FontAwesomeIcon icon={faLocationDot} />
                    <div>
                      <p>{data.circuit.toUpperCase()}</p>
                      <p className="nig-turq italic">
                        {t(`countries:${data.flag}`, { lng: "en" })}
                      </p>
                    </div>
                  </div>
                  <div className="flex">
                    <FontAwesomeIcon icon={faCalendar} />
                    <div>
                      <p>
                        <DateFormat
                          date={data.date}
                          format={"short_year"}
                          language={"en"}
                        />
                      </p>
                      <p className="nig-turq italic">{data.time} CET</p>
                    </div>
                  </div>
                  <div
                    className={`flex ${data.laps && String(data.laps).split(",").length > 1 ? "" : "nig-laps"}`}
                  >
                    <FontAwesomeIcon icon={faFlagCheckered} />
                    {(() => {
                      const lapsArray = data.laps
                        ? String(data.laps).split(",")
                        : [];

                      const hasMultiple = lapsArray.length > 1;

                      return (
                        <div>
                          {lapsArray[0] && (
                            <p>
                              {hasMultiple
                                ? `R1: ${lapsArray[0].trim()}`
                                : lapsArray[0].trim()}{" "}
                              LAPS
                            </p>
                          )}
                          {lapsArray[1] && (
                            <p>R2: {lapsArray[1].trim()} LAPS</p>
                          )}
                        </div>
                      );
                    })()}
                  </div>
                </div>
                <div className="nig-layout-container">
                  <div id="next-gp-container2">
                    <CircuitLayout
                      circuit_id={data.circuit_id}
                      id_canvas={`circuit-${data.circuit_id}`}
                      strokeWidth={3}
                    />
                  </div>
                </div>
                <div className="nig-keystats">
                  <p className="nig-turq italic">KEY STATS</p>
                  <div className="italic flex">
                    <div>
                      <div className="flex">
                        <FontAwesomeIcon icon={faTrophy} />
                        <div>
                          <p className="nig-turq">MOST WINS</p>
                          <p
                            className={
                              data.most_wins_driver != null &&
                              data.most_wins_driver.includes(" | ")
                                ? "longnames"
                                : ""
                            }
                          >
                            {data.most_wins_driver
                              ? data.most_wins_driver
                                  .split(" | ")
                                  .map((driverName) =>
                                    getAbbreviation(driverName.toUpperCase()),
                                  )
                                  .join(", ")
                              : "N/A"}
                          </p>
                          <p className="nig-turq">{data.most_wins}</p>
                        </div>
                      </div>
                      <div className="flex">
                        <img
                          src={require(`../../../assets/Images/laurel.png`)}
                        />
                        <div>
                          <p className="nig-turq">LAST WINNER</p>
                          <p>
                            {data.last_podium == null
                              ? "N/A"
                              : getAbbreviation(
                                  data.last_podium
                                    .split(" | ")[0]
                                    .toUpperCase(),
                                )}
                          </p>
                        </div>
                      </div>
                    </div>

                    <div>
                      <div className="flex">
                        <FontAwesomeIcon icon={faStopwatch} />
                        <div>
                          <p className="nig-turq">MOST POLES</p>
                          <p
                            className={
                              data.most_poles_driver != null &&
                              data.most_poles_driver.includes(" | ")
                                ? "longnames"
                                : ""
                            }
                          >
                            {data.most_poles_driver
                              ? data.most_poles_driver
                                  .split(" | ")
                                  .map((driverName) =>
                                    getAbbreviation(driverName.toUpperCase()),
                                  )
                                  .join(", ")
                              : "N/A"}
                          </p>
                          <p className="nig-turq">{data.most_poles}</p>
                        </div>
                      </div>

                      <div className="flex">
                        <img
                          src={require(`../../../assets/Images/helmet.png`)}
                        />
                        <div>
                          <p className="nig-turq">LAST POLE</p>
                          <p>
                            {data.last_pole_driver == null
                              ? "N/A"
                              : getAbbreviation(
                                  data.last_pole_driver.toUpperCase(),
                                )}
                          </p>
                          <p className="nig-turq">{data.last_pole_time}</p>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
                {data.last_podium != null && (
                  <div className="nig-lastrace">
                    <p className="nig-turq">LAST RACE</p>
                    <p>{data.last_event_name.toUpperCase()}</p>
                    <div className="flex">
                      <div>
                        <img src={require(`../../../assets/Images/pod1.png`)} />
                        <label>
                          {getAbbreviation(
                            data.last_podium.split(" | ")[0].toUpperCase(),
                          )}
                        </label>
                        <div className="barcolor b_gold"></div>
                      </div>
                      <div>
                        <img src={require(`../../../assets/Images/pod2.png`)} />
                        <label>
                          {getAbbreviation(
                            data.last_podium.split(" | ")[1].toUpperCase(),
                          )}
                        </label>
                        <div className="barcolor b_silver"></div>
                      </div>
                      <div>
                        <img src={require(`../../../assets/Images/pod3.png`)} />
                        <label>
                          {getAbbreviation(
                            data.last_podium.split(" | ")[2].toUpperCase(),
                          )}
                        </label>
                        <div className="barcolor b_bronze"></div>
                      </div>
                    </div>
                  </div>
                )}
              </div>
            </div>
          </div>
        )}

        {generatedImageUrl && (
          <div className="nig-preview-container mt-4">
            <img
              src={generatedImageUrl}
              alt="Preview Noticia GP"
              className="nig-preview-img"
              style={{ width: "100%", height: "auto", borderRadius: "8px" }}
            />
            <div className="mt-3">
              <Button variant="primary">
                <a
                  href={generatedImageUrl}
                  download={`GP_${data?.name}_Noticia.png`}
                  style={{ color: "white", textDecoration: "none" }}
                >
                  Download Final Image
                </a>
              </Button>
            </div>
          </div>
        )}
        {generatedImageUrl && <GeminiRequest text={geminiPromptText} />}
      </div>
    </div>
  );
};

export default PreGPGenerator;
